// src/features/client/AddClient.js
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import "./AddClient.css";
import { FaUser } from "react-icons/fa6";
import { IoIosAddCircleOutline } from "react-icons/io";
import { IoCloudUploadOutline } from "react-icons/io5";
import config from "../../config";
import React, { useEffect, useState } from "react";
import { VscChevronRight } from "react-icons/vsc";
import SuccessPopup from "../../components/common/SuccessPopup";
import ErrorPopup from "../../components/common/ErrorPopup";
import ConfirmPopup from "../../components/common/ConfirmPopup";
import { LuPencilLine } from "react-icons/lu";
import { GrNotification } from "react-icons/gr";
import { IoIosArrowBack } from "react-icons/io";
import Header from "../../components/common/Header";


const AddClients = ({ onCancel, selectedClient, onDelete, fetchClients }) => {
  const isViewModeInitial = !!selectedClient;
  const [isViewMode, setIsViewMode] = useState(isViewModeInitial);
  const [successPopup, setSuccessPopup] = useState(null);
  const [errorPopup, setErrorPopup] = useState(null);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [clientTypes, setClientTypes] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [absenceThresholds, setAbsenceThresholds] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [error, setError] = useState("");
  const [existingDocs, setExistingDocs] = useState([]);
  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";
    const orgName = localStorage.getItem("companyName") || "";

    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  useEffect(() => {
    if (selectedClient) {
      if (selectedClient.country) {
        setSelectedCountry(selectedClient.country);
      }
      if (selectedClient.state) {
        setSelectedState(selectedClient.state);
      }
    }
  }, [selectedClient]);

  useEffect(() => {
    const fetchExistingDocs = async () => {
      if (selectedClient && selectedClient.pdf && userDetails.token) {
        try {
          const response = await fetch(
            `${config.API_BASE_URL}${config.VIEW_PRESIGNED_URL_ENDPOINT}?filename=${encodeURIComponent(selectedClient.pdf)}`,
            {
              headers: {
                Authorization: `Bearer ${userDetails.token}`,
              },
            }
          );
          if (response.ok) {
            let viewUrl = await response.text();
            if (viewUrl.startsWith('"') && viewUrl.endsWith('"')) {
              viewUrl = JSON.parse(viewUrl);
            }
            setExistingDocs([
              {
                name: selectedClient.pdf.split("/").pop(),
                url: viewUrl,
                isExisting: true,
              },
            ]);
          }
        } catch (err) {
          console.error("Error fetching pre-signed view URL:", err);
        }
      } else {
        setExistingDocs([]);
      }
    };

    fetchExistingDocs();
  }, [selectedClient, userDetails.token]);

  const validationSchema = Yup.object({
    clientName: Yup.string()
      .required("Client Name is required.")
      .max(100, "Client Name should not exceed 100 characters.")
      .matches(
        /^[a-zA-Z\s\-'.]*$/,
        "Only letters, spaces, hyphens, apostrophes, or periods are allowed."
      )

      .max(100, "Max 100 characters.")
      .test(
        "no-leading-trailing-spaces",
        "No leading or trailing spaces",
        (value) => value?.trim() === value
      ),

    // Corporate Identification Number (CIN) – accept any 21 alphanumeric characters
    cin: Yup.string()
      .nullable()
      .transform((val) => (val ? val.toUpperCase().trim() : val))
      .matches(
        /^[A-Z0-9]{21}$/,
        "CIN must be exactly 21 alphanumeric characters."
      ),

    clientPhonenumber: Yup.string()
      .matches(
        /^\+91\s\d{10}$/,
        "Phone number must be in +91 XXXXXXXXXX format."
      )
      .required("Phone Number is required."),

    emailAddress: Yup.string()
      .email("Enter a valid email.")
      .test(
        "no-uppercase",
        "Email should not contain uppercase letters.",
        (value) => !value || value === value.toLowerCase()
      )
      .required("Email ID is required."),

    numberOfEmployees: Yup.string()
      .required("Company size is required."),

    websiteUrl: Yup.string()
      .nullable()
      .notRequired()
      .url("Please enter a valid URL."),

    address: Yup.string()
      .required("Address Line is required.")
      .max(100, "Max 100 characters."),

    city: Yup.string()
      .required("City is required.")
      .max(50, "Max 50 characters."),

    district: Yup.string()
      .required("District is required.")
      .max(50, "Max 50 characters."),

    state: Yup.string().required("State is required."),

    country: Yup.string().required("Country is required."),

    pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Pincode is required."),

    gst: Yup.string()
      .matches(/^[a-zA-Z0-9]{15}$/, "GSTIN must be 15 alphanumeric characters")
      .required("GSTIN is required."),

    clientType: Yup.string().required("Client Type is required."),

    primaryContactName: Yup.string()
      .max(50, "Primary Contact Name must be at most 50 characters.")
      .required("Primary Contact Name is required."),


    role: Yup.string()
      .required("Primary Contact Role is required.")
      .max(50, "Primary Contact Role must be at most 50 characters."),

    contactPhoneNumber: Yup.string()
      .matches(
        /^\+91\s\d{10}$/,
        "Phone number must be in +91 XXXXXXXXXX format."
      )
      .required("Primary Contact Phone Number is required."),

    contactEmailAddress: Yup.string()
      .email("Enter a valid email.")
      .test(
        "no-uppercase",
        "Email should not contain uppercase letters.",
        (value) => !value || value === value.toLowerCase()
      )
      .required("Primary Contact Email is required."),

    // Latitude: required, must be -90 to 90 with 6-16 decimals
    latitude: Yup.string()
      .required("Latitude is required.")
      .test(
        "lat-decimals-precision",
        "Latitude must have 6 to 16 decimal places and be between -90 and 90.",
        (value) => {
          if (value == null || value === "") return false;
          const trimmed = String(value).trim();
          // Match optional sign, 1-2 digits (or 90), decimal point, 6-16 decimals
          const regex = /^-?(?:[0-8]?\d|90)\.(\d{6,16})$/;
          if (!regex.test(trimmed)) return false;
          const num = parseFloat(trimmed);
          return !Number.isNaN(num) && num >= -90 && num <= 90;
        }
      ),

    // Longitude: required, must be -180 to 180 with 6-16 decimals
    longitude: Yup.string()
      .required("Longitude is required.")
      .test(
        "lng-decimals-precision",
        "Longitude must have 6 to 16 decimal places and be between -180 and 180.",
        (value) => {
          if (value == null || value === "") return false;
          const trimmed = String(value).trim();
          // Match optional sign, 1-3 digits (or 180), decimal point, 6-16 decimals
          const regex = /^-?(?:[0-9]?\d|1[0-7]\d|180)\.(\d{6,16})$/;
          if (!regex.test(trimmed)) return false;
          const num = parseFloat(trimmed);
          return !Number.isNaN(num) && num >= -180 && num <= 180;
        }
      ),

    workSite: Yup.string()
      .required("Work Site is required.")
      .max(200, "Work Site cannot exceed 200 characters."),
    legalDocuments: Yup.array()
      .min(1, "At least one legal document is required.")
      .required("Legal documents are required.")
      .test("fileSize", "Each file must be less than 5 MB.", (files) =>
        !files || files.every(file => file.isExisting || file.size <= 5 * 1024 * 1024)
      )
      .test("fileType", "Only PDF, JPEG, and PNG are allowed.", (files) =>
        !files || files.every(file => file.isExisting || ["application/pdf", "image/jpeg", "image/png"].includes(file.type))
      ),

    // Proximity Radius: required, positive number up to 500 meters
    proximityRadius: Yup.string()
      .required("Proximity Radius is required.")
      .test(
        "proximity-radius-validation",
        "Must be a valid positive number. Maximum value: 500 meters",
        (value) => {
          if (value == null || value === "") return false;
          const num = Number(String(value).trim());
          return !Number.isNaN(num) && num > 0 && num <= 500;
        }
      ),

    absenceThreshold: Yup.string().required(
      "Employee Absence Threshold is required."
    ),
  });

  const blankInitialValues = {
    clientName: "",
    cin: "",
    clientPhonenumber: "+91 ",
    emailAddress: "",
    gst: "",
    clientType: "",
    numberOfEmployees: "",
    websiteUrl: "",
    paymentTerms: "",
    primaryContactName: "",
    role: "",
    contactPhoneNumber: "+91 ",
    contactEmailAddress: "",
    address: "",
    city: "",
    district: "",
    state: "",
    pincode: "",
    country: "",
    latitude: "",
    longitude: "",
    workSite: "",
    proximityRadius: "",
    absenceThreshold: "",
    legalDocuments: [],
  };

  // Map selectedClient response into form fields for edit mode
  const editInitialValues = selectedClient
    ? {
      clientName: selectedClient.clientName || "",
      cin: selectedClient.cinNumber || "",
      clientPhonenumber: selectedClient.clientPhonenumber || "+91 ",
      emailAddress:
        selectedClient.clientEmailaddress ||
        selectedClient.emailAddress ||
        "",
      gst: selectedClient.gstNumber || "",
      clientType: selectedClient.clientType || "",
      numberOfEmployees: selectedClient.companySize || "",
      websiteUrl: selectedClient.companyWebsite || "",
      paymentTerms: selectedClient.paymentTerms || "",
      primaryContactName: selectedClient.primaryContactname || "",
      role: selectedClient.role || "",
      contactPhoneNumber:
        selectedClient.phoneNumber ||
        selectedClient.clientPhonenumber ||
        "+91 ",
      contactEmailAddress: selectedClient.emailAddress || "",
      address: selectedClient.address || "",
      city: selectedClient.city || "",
      district: selectedClient.district || "",
      state: selectedClient.state || "",
      pincode: selectedClient.pincode
        ? String(selectedClient.pincode).split(".")[0]
        : "",
      country: selectedClient.country || "",
      latitude:
        selectedClient.latitude !== null &&
          selectedClient.latitude !== undefined
          ? String(selectedClient.latitude)
          : "",
      longitude:
        selectedClient.longitude !== null &&
          selectedClient.longitude !== undefined
          ? String(selectedClient.longitude)
          : "",
      workSite: selectedClient.workSite || "",
      proximityRadius:
        selectedClient.proximityRadius !== null &&
          selectedClient.proximityRadius !== undefined
          ? String(selectedClient.proximityRadius)
          : "",
      absenceThreshold: selectedClient?.absenceThreshold
        ? String(selectedClient.absenceThreshold).trim()
        : "",
      legalDocuments: existingDocs,
    }
    : blankInitialValues;

  // Helper to convert File to Base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]); // Get only Base64 data
      reader.onerror = (error) => reject(error);
    });
  };

  const uploadFileWithPresignedUrl = async (file) => {
    try {
      const filename = `Client/${file.name}`;
      console.log("Generating pre-signed URL for:", filename);

      // 1. Get Pre-signed URL
      const presignedUrlResponse = await fetch(
        `${config.API_BASE_URL}${config.GENERATE_PRESIGNED_URL_ENDPOINT}?filename=Client/${encodeURIComponent(file.name)}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
          },
        }
      );

      if (!presignedUrlResponse.ok) {
        throw new Error("Failed to generate pre-signed URL");
      }

      // The backend response is just a string (the URL)
      let uploadUrl = await presignedUrlResponse.text();
      // If it's a JSON-wrapped string, parse it
      if (uploadUrl.startsWith('"') && uploadUrl.endsWith('"')) {
        uploadUrl = JSON.parse(uploadUrl);
      }

      if (!uploadUrl) {
        throw new Error("No upload URL returned from pre-signed URL API");
      }

      console.log("Uploading file to S3:", uploadUrl);

      // 2. Upload file to S3 via PUT
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file to pre-signed URL");
      }

      return filename; // e.g. "Client/heidi.jpg"
    } catch (error) {
      console.error("Error in uploadFileWithPresignedUrl:", error);
      throw error;
    }
  };

  // 👉 Create + Update
  const handleSubmit = async (values) => {
    console.log("Form values:", values);

    // Convert files to Base64 AND upload via Pre-signed URL
    let legalDocumentsPayload = [];

    try {
      if (values.legalDocuments && values.legalDocuments.length > 0) {
        legalDocumentsPayload = await Promise.all(
          values.legalDocuments.map(async (file) => {
            // Existing logic: keep existing file
            if (file.isExisting) {
              return {
                pdf: selectedClient.pdf,
                type: selectedClient.pdf.split(".").pop(),
              };
            }

            // New logic: Upload to pre-signed URL
            const fileKey = await uploadFileWithPresignedUrl(file);

            return {
              pdf: fileKey, // e.g., "Client/heidi.jpg"
              type: file.name.split(".").pop(), // e.g., "jpg", "pdf", "png"
            };
          })
        );
      }
    } catch (err) {
      console.error("Error processing/uploading files:", err);
      setErrorMsg("Failed to upload legal documents via pre-signed URL.");
      // You can return or throw here if you want to abort the client submission
      return;
    }

    const requestBody = {
      organisation: {
        orgId: userDetails.orgId,
      },
      clientName: values.clientName,
      cinNumber: values.cin,
      clientPhonenumber: values.clientPhonenumber,
      clientEmailaddress: values.emailAddress,
      gstNumber: values.gst,
      clientType: values.clientType,
      companySize: values.numberOfEmployees,
      companyWebsite: values.websiteUrl,
      paymentTerms: values.paymentTerms,
      primaryContactname: values.primaryContactName,
      role: values.role,
      emailAddress: values.emailAddress,
      address: values.address,
      city: values.city,
      district: values.district,
      state: values.state,
      pincode: values.pincode,
      country: values.country,
      latitude: values.latitude,
      longitude: values.longitude,
      workSite: values.workSite,
      proximityRadius: 10.0,
      absenceThreshold: Number(values.absenceThreshold),
      pdf: legalDocumentsPayload.length > 0 ? legalDocumentsPayload[0].pdf : "",
      type: legalDocumentsPayload.length > 0 ? legalDocumentsPayload[0].type : "",
      status: "Active",
    };

    const isEdit = !!selectedClient;
    const clientKey =
      selectedClient?.clientKey ??
      (selectedClient?.id != null ? String(selectedClient.id) : null);

    const url = isEdit
      ? `${config.API_BASE_URL}${config.CLIENT_ENDPOINT}/${clientKey}`
      : `${config.API_BASE_URL}${config.CLIENT_ENDPOINT}`;
    const method = isEdit ? "PUT" : "POST";

    console.log("Request method:", method);
    console.log("Request URL:", url);
    console.log("Raw request body:", requestBody);

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        credentials: "include",
      });

      console.log("Raw response:", response);

      const text = await response.text();
      console.log("Raw response text:", text);

      if (!response.ok) {
        setErrorMsg(`Unexpected response: ${text}`);
        setErrorPopup(`Unexpected response: ${text}`);
        return;
      }

      if (!isEdit && text.includes("Client created successfully")) {
        setSuccessPopup("Client registered successfully!");
        fetchClients();
      } else if (isEdit && text.includes("Client updated successfully")) {
        setSuccessPopup("Client updated successfully!");
      } else {
        setSuccessPopup(
          isEdit
            ? "Client updated successfully!"
            : "Client registered successfully!"
        );
      }
    } catch (error) {
      console.error("Network or server error:", error);
      setErrorMsg("Something went wrong. Please try again.");
      setErrorPopup("Something went wrong. Please try again.");
    }
  };

  // 👉 Delete handler – calls DELETE /{clientKey}
  const handleDelete = () => {
    if (!selectedClient) {
      setErrorPopup("No client selected to delete.");
      return;
    }

    const clientKey =
      selectedClient.clientKey ??
      (selectedClient.id != null ? String(selectedClient.id) : null);

    if (!clientKey) {
      setErrorPopup("No valid client key/id found for this client.");
      return;
    }

    setShowConfirmPopup(true);
  };

  const handleDeleteConfirm = async () => {
    setShowConfirmPopup(false);

    if (!selectedClient) {
      setErrorPopup("No client selected to delete.");
      return;
    }

    const clientKey =
      selectedClient.clientKey ??
      (selectedClient.id != null ? String(selectedClient.id) : null);

    if (!clientKey) {
      setErrorPopup("No valid client key/id found for this client.");
      return;
    }

    const url = `${config.API_BASE_URL}${config.CLIENT_ENDPOINT}/${clientKey}`;
    console.log("Deleting client at URL:", url);

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      console.log("Delete response:", response);

      const text = await response.text();
      console.log("Delete response text:", text);

      if (!response.ok) {
        setErrorMsg(`Unexpected response: ${text}`);
        setErrorPopup(`Unexpected response: ${text}`);
        return;
      }

      if (text.includes("Client deleted successfully")) {
        setSuccessPopup("Client deleted successfully!");
      } else {
        setSuccessPopup("Client deleted successfully!");
      }

      if (onDelete) {
        onDelete(selectedClient);
      }
    } catch (err) {
      console.error("Delete client error:", err);
      setErrorMsg("Failed to delete client. Please try again.");
      setErrorPopup("Failed to delete client. Please try again.");
    }
  };

  const validateFiles = (files, currentFiles = []) => {
    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
    ];

    const maxSize = 5 * 1024 * 1024; // 5MB

    let validFiles = [];
    let errorMessage = "";

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errorMessage = "Only JPEG, PNG, and PDF files are allowed.";
        return;
      }

      if (file.size > maxSize) {
        errorMessage = "Each file must be less than 5MB.";
        return;
      }

      validFiles.push(file);
    });

    if (errorMessage) {
      setErrorPopup(errorMessage);
    }

    return [...currentFiles, ...validFiles];
  };



  const fetchDropdownOptions = async (type, setter) => {
    try {
      const response = await fetch(
        `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=${type}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to fetch " + type);
      const data = await response.json();
      const activeItems = data.filter((item) => item.isActive);
      setter(activeItems);
    } catch (err) {
      console.error(`Error fetching ${type}:`, err);
    }
  };

  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    fetchDropdownOptions("ClientType", setClientTypes);
    fetchDropdownOptions("CompanySize", setCompanySizes);
    fetchDropdownOptions("PaymentTerms", setPaymentTerms);
    fetchDropdownOptions("AbsenceThreshold", setAbsenceThresholds);
  }, [userDetails]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}${config.COUNTRY_ENDPOINT}`,
          {
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch countries");
        const data = await res.json();
        console.log("Countries API Response:", data);
        const countriesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        if (countriesList.length > 0) {
          console.log("First Country Object Keys:", Object.keys(countriesList[0]));
          console.log("First Country Object Values:", countriesList[0]);
        }
        setCountries(countriesList);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    if (!selectedCountry) return;

    const fetchStates = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.STATE_ENDPOINT}?countryId=${selectedCountry}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!response.ok) throw new Error("Failed to fetch states");
        const data = await response.json();
        console.log("States API Response:", data);
        const statesList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        if (statesList.length > 0) {
          console.log("First State Object Keys:", Object.keys(statesList[0]));
          console.log("First State Object Values:", statesList[0]);
        }
        setStates(statesList);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchStates();
  }, [selectedCountry]);

  useEffect(() => {
    if (!selectedState) return;

    const fetchDistricts = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.DISTRICT_ENDPOINT}?stateId=${selectedState}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
          }
        );
        if (!response.ok) throw new Error("Failed to fetch districts");
        const data = await response.json();
        console.log("Districts API Response:", data);
        const districtsList = Array.isArray(data) ? data : (data?.body || data?.data || []);
        if (districtsList.length > 0) {
          console.log("First District Object Keys:", Object.keys(districtsList[0]));
          console.log("First District Object Values:", districtsList[0]);
        }
        setDistricts(districtsList);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchDistricts();
  }, [selectedState]);

  const isEditMode = !!selectedClient;

  return (
    <div>
      {successPopup && (
        <SuccessPopup
          message={successPopup}
          onClose={() => {
            setSuccessPopup(null);
            onCancel();
          }}
        />
      )}
      {errorPopup && (
        <ErrorPopup message={errorPopup} onClose={() => setErrorPopup(null)} />
      )}
      {showConfirmPopup && (
        <ConfirmPopup
          message="Are you sure you want to delete this client?"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowConfirmPopup(false)}
        />
      )}

      <Header
        showBack={true}
        onBack={onCancel}
        title={isEditMode ? "Edit Client" : "Add Client"}
        subtitle={
          <>
            All Client <VscChevronRight style={{ verticalAlign: "middle" }} />{" "}
            {isEditMode ? "Edit Client" : "Add Client"}
          </>
        }
      />

      <Formik
        enableReinitialize
        initialValues={editInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <Form className='add-client-form'>
            {isEditMode && (
              <div className='client-form-action-bar'>
                <button
                  type='button'
                  className='client-delete-btn'
                  onClick={handleDelete}
                >
                  <span className='client-delete-icon'>🗑</span>
                  <span>Delete</span>
                </button>

                <button
                  type='button'
                  className='client-edit-btn'
                  onClick={() => setIsViewMode(!isViewMode)}
                >
                  <span className='client-edit-icon'><LuPencilLine /></span>
                  <span>{isViewMode ? "Edit" : "View"}</span>
                </button>
              </div>
            )}

            {/* Client Information Section */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Client Information
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Client Name <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='clientName' disabled={isViewMode} />
                  <ErrorMessage
                    name='clientName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>Corporate Identification Number (CIN)</label>
                  <Field type='text' name='cin' disabled={isViewMode} />
                  <ErrorMessage name='cin' component='div' className='error' />
                </div>
                <div className='input-group'>
                  <label>
                    Phone Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='clientPhonenumber'
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      let value = e.target.value;

                      if (!value.startsWith("+91 ")) {
                        value = "+91 " + value.slice(4);
                      }

                      if (value.length > 12) {
                        value = value.slice(0, 14);
                      }

                      setFieldValue("clientPhonenumber", value);
                    }}
                  />
                  <ErrorMessage
                    name='clientPhonenumber'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Email Address <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='email'
                    name='emailAddress'
                    disabled={isViewMode}
                  />
                  <ErrorMessage
                    name='emailAddress'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    GST IN <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='gst' disabled={isViewMode} />
                  <ErrorMessage name='gst' component='div' className='error' />
                </div>
                <div className='input-group'>
                  <label>
                    Client Type <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='clientType' disabled={isViewMode}>
                    <option value=''>Select</option>
                    {clientTypes.map((type) => (
                      <option key={type.id} value={type.values}>
                        {type.values}
                      </option>
                    ))}
                  </Field>
                </div>
                <div className='input-group'>
                  <label>
                    Company Size <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='numberOfEmployees'
                    disabled={isViewMode}
                  >
                    <option value=''>Select</option>
                    {companySizes.map((size) => (
                      <option key={size.id} value={size.values}>
                        {size.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='numberOfEmployees'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>Company Website</label>
                  <Field type='url' name='websiteUrl' disabled={isViewMode} />
                  <ErrorMessage
                    name='websiteUrl'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>Payment Terms</label>
                  <Field as='select' name='paymentTerms' disabled={isViewMode}>
                    <option value=''>Select</option>
                    {paymentTerms.map((term) => (
                      <option key={term.id} value={term.values}>
                        {term.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='paymentTerms'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Primary Contact Section */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>
                <span>Primary Contact</span>
                <span className='add-icon'>
                  <IoIosAddCircleOutline />
                </span>
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Primary Contact Name
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='primaryContactName'
                    disabled={isViewMode}
                  />
                  <ErrorMessage
                    name='primaryContactName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group half-width'>
                  <label>
                    Primary Contact Role <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='role'
                    disabled={isViewMode}
                  />
                  <ErrorMessage
                    name='role'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Phone Number <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='contactPhoneNumber'
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      let value = e.target.value;

                      if (!value.startsWith("+91 ")) {
                        value = "+91 " + value.slice(4);
                      }

                      if (value.length > 12) {
                        value = value.slice(0, 14);
                      }

                      setFieldValue("contactPhoneNumber", value);
                    }}
                  />
                  <ErrorMessage
                    name='contactPhoneNumber'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Email ID<span className='required-asterisk'>*</span>
                  </label>
                   <Field
                    type='email'
                    name='contactEmailAddress'
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      setFieldValue("contactEmailAddress", e.target.value.toLowerCase());
                    }}
                  />
                  <ErrorMessage
                    name='contactEmailAddress'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Billing Address Section */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Billing Address
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Country <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='country'
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      const countryCode = e.target.value;
                      setFieldValue("country", countryCode);
                      setSelectedCountry(countryCode);
                      setFieldValue("state", "");
                      setFieldValue("district", "");
                      setStates([]);
                      setDistricts([]);
                    }}
                  >
                    <option value=''>Select</option>
                    {countries.map((country) => (
                      <option key={country.id || country.countryId} value={country.id || country.countryId}>
                        {country.countryName || country.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='country'
                    component='div'
                    className='error'
                  />
                </div>

                {/* State Dropdown */}
                <div className='input-group'>
                  <label>
                    State <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='state'
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;
                      const stateCode = e.target.value;
                      setFieldValue("state", stateCode);
                      setSelectedState(stateCode);
                      setFieldValue("district", "");
                      setDistricts([]);
                    }}
                  >
                    <option value=''>Select</option>
                    {states.map((state) => (
                      <option key={state.id || state.stateId} value={state.id || state.stateId}>
                        {state.countryName || state.stateName || state.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='state'
                    component='div'
                    className='error'
                  />
                </div>

                {/* District Dropdown */}
                <div className='input-group'>
                  <label>
                    District <span className='required-asterisk'>*</span>
                  </label>
                  <Field as='select' name='district' disabled={isViewMode}>
                    <option value=''>Select</option>
                    {districts.map((district) => (
                      <option key={district.id || district.districtId} value={district.id || district.districtId}>
                        {district.countryName || district.districtName || district.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='district'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    City <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='city' disabled={isViewMode} />
                  <ErrorMessage name='city' component='div' className='error' />
                </div>

                <div className='input-group'>
                  <label>
                    Address Line / Street
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='address' disabled={isViewMode} />
                  <ErrorMessage
                    name='address'
                    component='div'
                    className='error'
                  />
                </div>

                <div className='input-group'>
                  <label>
                    Pincode <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='pincode' disabled={isViewMode} />
                  <ErrorMessage
                    name='pincode'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Geo Location Section */}
            <section className='form-section'>
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>
                <span>Geo Location</span>
                <span className='add-icon'>
                  <IoIosAddCircleOutline />
                </span>
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Latitude <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='latitude' disabled={isViewMode} />
                  <ErrorMessage
                    name='latitude'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Longitude <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='longitude' disabled={isViewMode} />
                  <ErrorMessage
                    name='longitude'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Work Site <span className='required-asterisk'>*</span>
                  </label>
                  <Field type='text' name='workSite' disabled={isViewMode} />
                  <ErrorMessage
                    name='workSite'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Proximity Radius
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    type='text'
                    name='proximityRadius'
                    disabled={isViewMode}
                  />
                  <ErrorMessage
                    name='proximityRadius'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            <section className='form-section'>
              {/* Employee Absence Threshold Section */}
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Employee Absence Threshold
              </h3>
              <div className='form-grid'>
                <div className='input-group'>
                  <label>
                    Absence Threshold
                    <span className='required-asterisk'>*</span>
                  </label>
                  <Field
                    as='select'
                    name='absenceThreshold'
                    disabled={isViewMode}
                  >
                    <option value=''>Select</option>
                    {absenceThresholds.map((threshold) => (
                      <option key={threshold.id} value={String(threshold.values).trim()}>
                        {threshold.values}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name='absenceThreshold'
                    component='div'
                    className='error'
                  />
                </div>
              </div>

              {/* Legal Documents Section */}
              <h3>
                <span className='icon'>
                  <FaUser />
                </span>{" "}
                Legal Documents <span className='required-asterisk'>*</span>
              </h3>
              <div className='file-upload-container'>
                <div
                  className='file-upload-box'
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    if (isViewMode) {
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    const currentFiles = values.legalDocuments || [];
                    setFieldValue("legalDocuments", [...currentFiles, ...files]);
                  }}
                >
                  <label htmlFor='file-upload' className='upload-link'>
                    <span className='upload-icon' style={{ marginLeft: '48%' }}>
                      <IoCloudUploadOutline />
                    </span>
                    <p>Drag & Drop or <span className='choose-file'>Documents</span> to upload</p>
                  </label>

                  <input
                    id='file-upload'
                    name='legalDocuments'
                    type='file'
                    multiple
                    accept='.pdf,.jpeg,.jpg,.png'
                    disabled={isViewMode}
                    onChange={(e) => {
                      if (isViewMode) return;

                      const files = Array.from(e.target.files);
                      const currentFiles = values.legalDocuments || [];

                      const validatedFiles = validateFiles(files, currentFiles);
                      setFieldValue("legalDocuments", validatedFiles);
                    }}
                    style={{ display: "none" }}
                  />

                  <p className='file-support-text'>
                    Supported formats: Jpeg, png, pdf (Max 5MB)
                  </p>

                  <div className='uploaded-files'>
                    <h4>Uploaded Files:</h4>
                    <ul>
                      {Array.isArray(values.legalDocuments) &&
                        values.legalDocuments.map((file, index) => (
                          <li key={index} className='file-item'>
                            {file.isExisting ? (
                              <a
                                href={file.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='existing-file-link'
                              >
                                {file.name} (View)
                              </a>
                            ) : (
                              <span>{file.name}</span>
                            )}
                            {!isViewMode && (
                              <button
                                type='button'
                                className='remove-file-btn'
                                onClick={() => {
                                  const newFiles = values.legalDocuments.filter((_, i) => i !== index);
                                  setFieldValue("legalDocuments", newFiles);
                                }}
                              >
                                &times;
                              </button>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>

                  <ErrorMessage
                    name='legalDocuments'
                    component='div'
                    className='error'
                  />
                </div>
              </div>
            </section>

            {/* Action Buttons */}
            <div className='button-container'>
              {/* Cancel – LEFT */}
              <button
                type='button'
                className='cancel-btn'
                onClick={onCancel}
              >
                Cancel
              </button>

              {/* Add / Update Client – RIGHT */}
              <button
                type='submit'
                disabled={isSubmitting || isViewMode}
                className='save-btn'
              >
                {isSubmitting
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                    ? "Update Client"
                    : "Add Client"}
              </button>
            </div>

          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddClients;
